import { useLedger } from '../hooks/useLedger';
import { useGroupExpenses } from '../hooks/queries/useGroupExpenses';
import { getUserData } from '../lib/client';
import { formatCurrency } from '../utils/formatters';
import { useNavigate, useParams } from 'react-router-dom';
import { useAllExpenseParticipations } from '../hooks/queries/useAllExpenseParticipations';
import { useEffect, useState } from 'react';
import { useGroupMembers } from '../hooks/queries/useGroupMembers';
import { useGroupInfo } from '../hooks/queries/useGroupInfo';
import Header from '../components/Header';
import { icons } from '../assets/icons';

export const LedgerPage = () => {
  const ALL_VALUE = "__all__";
  const {id: groupId} = useParams<{id: string}>();
  const { data: group, isLoading: groupLoading, isError: groupError } = useGroupInfo(groupId);
  const { data: allExpenseParticipations, isLoading: partiLoading, isError:partiError } = useAllExpenseParticipations(groupId);
  const { data: allExpenses,isLoading: expLoading, isError:expError } = useGroupExpenses(groupId, getUserData() , true)
  const { data: members, isLoading: membersLoading, isError: membersError } = useGroupMembers(groupId);

  const [selectedMember, setSelectedMember] = useState<string>("");
  const selectedMemberName = selectedMember === ALL_VALUE
    ? '전체'
    : members?.find(member => member.id === selectedMember)?.user_name || '전체';
  const {ledger, totalSpending} = useLedger(allExpenses, allExpenseParticipations, selectedMember);
  
  const currentUser = getUserData();
  const groupCurrency = group?.default_currency
  const navigate = useNavigate()

  useEffect(() => {
    const currentUserMemberId = members?.find(member => member.user_id === currentUser?.id)?.id || null;
    // 최초 진입 시에만 기본값을 '본인'으로 세팅 (사용자가 '전체' 등으로 바꾼 경우 덮어쓰지 않음)
    if (!selectedMember && currentUserMemberId) {
      setSelectedMember(currentUserMemberId)
    }
  }, [members, currentUser?.id, selectedMember]);


  const isLoading = partiLoading || groupLoading || membersLoading ||expLoading;
  const isError = partiError || groupError || membersError || expError;

    if (isError || !groupId) {
    return <div className="container mx-auto px-4 py-8">
      <span>그룹 정보를 불러오는데 실패했습니다.</span>
      <button className='text-blue-900 flex items-center gap-1 underline  ' onClick={() => navigate(-1)}>목록으로</button>
    </div>;
  }

  return (
    <div className="container mx-auto px-4 pt-16 pb-8">
      <Header title={group?.name || '...'} showBackButton onBackClick={() => navigate(-1)} />

      <div className='flex justify-between items-center gap-3 mb-4'>
        <h1 className="shrink-0 text-2xl font-bold align-baseline">사용한 금액</h1>

        <div className="relative inline-flex min-w-0 h-9 items-center gap-2 rounded-lg bg-gray-50 px-2.5 focus-within:ring-2 focus-within:ring-blue-500">
          <span aria-hidden="true" className="truncate">{selectedMemberName}</span>
          <img src={icons.chevronDown} alt="" aria-hidden="true" className="w-4 h-4 shrink-0" />
          <select
            aria-label="원장 조회 대상"
            title={selectedMemberName}
            className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
            style={{ padding: 0 }}
            value={selectedMember || ALL_VALUE}
            onChange={(e) => setSelectedMember(e.target.value)}
          >
            <option value={ALL_VALUE}>전체</option>
            {members?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.user_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {
        isLoading && (
          <div className="flex justify-center items-center py-16">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )
      }

      {selectedMember && (
        <div className="w-full border-collapse border rounded-xl overflow-hidden bg-[#E1EDFF] border-[#E1EDFF]">
          <div className='flex justify-between text-lg font-bold px-3 py-2'>
            <span>총 지출</span>
            <span className='text-blue-500 font-mono'>{!groupCurrency ? '...' : formatCurrency(totalSpending, groupCurrency)}</span>
          </div>
          <div className='flex flex-col p-3 gap-3 bg-white rounded-xl'>
            {ledger.map((row, idx) => (
              <div key={idx} className='flex flex-col gap-3'>
                {row.showDate && idx !== 0 && (
                  <div className="h-[1px] bg-gray-200"/>
                )}
                {row.showDate && (
                  <div className="text-gray-400">
                    {row.date}
                  </div>
                )}
                {(row as any).isDailyTotal ? (
                  <div className="flex justify-end items-center -mt-0.5 pt-1.5 border-dotted border-t border-gray-50">
                    <div className="font-bold font-mono text-right text-blue-600">
                      {!groupCurrency ? '...' : formatCurrency(row.amount, groupCurrency as any)}
                    </div>
                  </div>
                ) : (
                  <div className='flex justify-between'>
                    <div className="font-medium">{row.description?.substring(0,20) || ''}</div>
                    <div className="font-bold font-mono text-right">
                      {/* 그룹 기본 통화로 환산된 금액 */}
                      {formatCurrency(row.amount, groupCurrency as any)}
                      {/* 원래 통화가 그룹 기본 통화와 다른 경우 원래 통화 금액도 표시 */}
                      {row.currency !== groupCurrency && (
                        <div className="text-sm text-gray-500 mt-0">
                          {formatCurrency(row.amount_in_currency, row.currency as any)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
